import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { callFunction } from "../api/client";
import { Button } from "../ui/Button";
import { Fact } from "../ui/Facts";
import { Working } from "./Spinner";
import { jobRunning, jobSummary, useJob } from "./JobProgress";

/* Ask Apollo about this one record.
 *
 * A one-off button on a person and on a company, pressed when somebody wants
 * this record filled in. There is no timer behind it and nothing queues
 * itself.
 *
 * Email, LinkedIn and title arrive with the job. A phone number does not:
 * Apollo posts one to the webhook afterwards, so it lands on the record later
 * and is not part of this job's result.
 *
 * Apollo is an outbound call and Postgres does not make those, so the press
 * queues a lake.job and the worker does the asking. Same mechanism as campaign
 * runs and exports, and the Jobs screen shows the same rows.
 *
 * On the Details tab this is one fact, "Apollo", in the group whose facts it
 * fills: the ways to reach a person, or what EVIE knows about a company. */

/* A press costs credits, so the button says so before it is pressed rather
   than after. A company press enriches up to ten people. */
const WHAT_IT_DOES: Record<string, string> = {
  person: "Adds email, title and LinkedIn.",
  company: "Adds contacts in buying roles.",
};

export function ApolloFact({
  token, entityType, entityId, onFilled,
}: {
  token: string;
  entityType: "person" | "company";
  entityId: string;
  onFilled: () => void;
}) {
  const [jobId, setJobId] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);

  const ask = useMutation({
    mutationFn: () =>
      callFunction<string>(token, "request_apollo_fill", {
        p_entity_type: entityType,
        p_entity_id: entityId,
      }),
    /* The function returns the job already running when there is one, rather
       than buying the same answer twice on a double-click, so this treats both
       cases the same way. */
    onSuccess: (id) => { setJobId(id); setSaid(null); },
    onError: (e: Error) => setSaid(e.message),
  });

  const job = useJob(token, jobId);
  const status = job.data?.status ?? null;

  /* The record reloads itself when the job finishes, because the point of the
     button is what it wrote and asking somebody to reload is asking them to do
     the last step by hand. */
  useEffect(() => {
    if (status === "done") onFilled();
    // onFilled is a refetch, and depending on it would rerun this whenever the
    // parent rerendered.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const running = jobRunning(jobId, status);

  return (
    <Fact label="Apollo enrichment" id="apollo">
      <span className="fact-tool">
        <Button kind="link" size="compact-sm" onClick={() => ask.mutate()} disabled={ask.isPending || running}>
          Run Apollo enrichment
        </Button>
        <span className="muted">{WHAT_IT_DOES[entityType]}</span>
      </span>
      {said ? <span className="refusal fact-line">{said}</span> : null}
      {/* What Apollo found, and not only that it finished. "Done" left a rep
          unable to tell a match from a miss. James, 2026-09-25. */}
      {status === "done" ? (
        jobSummary(job.data).length
          ? jobSummary(job.data).map((line) => <span key={line} className="fact-line">{line}</span>)
          : <span className="muted fact-line">Done.</span>
      ) : status === "failed" ? (
        <span className="refusal fact-line">{job.data?.error || "Failed."}</span>
      ) : status === "cancelled" ? (
        <span className="muted fact-line">Cancelled.</span>
      ) : running ? (
        <span className="muted fact-line"><Working when>Running Apollo enrichment...</Working></span>
      ) : null}
    </Fact>
  );
}
